import React, { useCallback, useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { fmt } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle, RotateCcw, ChevronDown, ChevronUp, Users, Wallet, Download, Printer, Plus } from "lucide-react";
import { printReport } from "@/lib/printReport";
import { usePrintTemplate } from "@/hooks/usePrintTemplate";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { monthEnd, monthOptions, monthStart } from "@/lib/payPeriod";

export default function StaffPayouts() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const template = usePrintTemplate();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [customRange, setCustomRange] = useState(false);
  const [fromDate, setFromDate] = useState(monthStart(new Date().toISOString().slice(0, 7)));
  const [toDate, setToDate] = useState(monthEnd(new Date().toISOString().slice(0, 7)));
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [data, setData] = useState({ rows: [], totals: {} });
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [adjModal, setAdjModal] = useState(null);
  const [adjForm, setAdjForm] = useState({ type: "bonus", amount: 0, reason: "" });

  const periodStart = customRange ? fromDate : monthStart(month);
  const periodEnd = customRange ? toDate : monthEnd(month);

  const load = useCallback(async () => {
    const [prev, accts, hist] = await Promise.all([
      api.get(`/staff-payouts/preview?period_start=${periodStart}&period_end=${periodEnd}`),
      api.get("/accounts").catch(() => ({ data: [] })),
      api.get(`/staff-payouts?period_start=${periodStart}&period_end=${periodEnd}`).catch(() => ({ data: { items: [] } })),
    ]);
    setData(prev.data);
    setHistory((hist.data?.items || []).filter((p) => p.status === "confirmed"));
    setAccounts(accts.data || []);
    if (!accountId && accts.data?.length) {
      const def = accts.data.find((a) => a.is_default) || accts.data[0];
      setAccountId(def.account_id);
    }
  }, [periodStart, periodEnd, accountId]);

  useEffect(() => { load(); }, [load]);

  const confirmPayout = async (employeeUserId) => {
    if (!accountId) {
      toast.error(lang === "ar" ? "اختر صندوق الدفع" : "Select payout account");
      return;
    }
    const ok = await confirm({
      title: lang === "ar" ? "تأكيد صرف العمولات والإكراميات؟" : "Confirm staff payout?",
      confirmLabel: lang === "ar" ? "تأكيد" : "Confirm",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "primary",
    });
    if (!ok) return;
    try {
      await api.post(`/staff-payouts/${employeeUserId}/draft`, { period_start: periodStart, period_end: periodEnd });
      await api.post(`/staff-payouts/${employeeUserId}/confirm`, {
        period_start: periodStart,
        period_end: periodEnd,
        account_id: accountId,
      });
      toast.success(t("confirm_payout"));
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    }
  };

  const confirmAll = async () => {
    if (!accountId) {
      toast.error(lang === "ar" ? "اختر صندوق الدفع" : "Select payout account");
      return;
    }
    const ok = await confirm({
      title: lang === "ar" ? "تأكيد الكل؟" : "Confirm all open payouts?",
      confirmLabel: lang === "ar" ? "تأكيد" : "Confirm",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "primary",
    });
    if (!ok) return;
    try {
      const r = await api.post("/staff-payouts/confirm-all", {
        period_start: periodStart,
        period_end: periodEnd,
        account_id: accountId,
      });
      toast.success(`${t("confirm_payout")}: ${(r.data.confirmed || []).length}`);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} failed`);
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    }
  };

  const unconfirm = async (payoutId) => {
    const ok = await confirm({
      title: lang === "ar" ? "إلغاء التأكيد؟" : "Unconfirm payout?",
      confirmLabel: lang === "ar" ? "تأكيد" : "Confirm",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.post(`/staff-payouts/${payoutId}/unconfirm`);
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    }
  };

  const addAdjustment = async () => {
    if (!parseFloat(adjForm.amount)) return;
    try {
      await api.post(`/staff-payouts/${adjModal}/draft`, { period_start: periodStart, period_end: periodEnd });
      await api.post(`/staff-payouts/${adjModal}/adjustments`, {
        ...adjForm,
        amount: parseFloat(adjForm.amount),
        period_start: periodStart,
        period_end: periodEnd,
      });
      setAdjModal(null);
      setAdjForm({ type: "bonus", amount: 0, reason: "" });
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    }
  };

  const exportCsv = () => {
    const rows = [["Employee", "Commission", "Tips", "Bonuses", "Deductions", "Net", "Status"]];
    data.rows.forEach((r) => {
      rows.push([
        r.employee_name,
        r.commission_amount,
        r.tips_amount,
        r.total_bonuses || 0,
        r.total_deductions || 0,
        r.net,
        r.status,
      ]);
    });
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `staff_payouts_${month}.csv`;
    a.click();
  };

  const printReportPro = () => {
    printReport({
      title: t("nav_staff_payouts"),
      subtitle: `${periodStart} → ${periodEnd}`,
      lang,
      template,
      columns: [
        lang === "ar" ? "الموظف" : "Employee",
        t("commission_owed"),
        t("tips_owed"),
        lang === "ar" ? "الصافي" : "Net",
        lang === "ar" ? "الحالة" : "Status",
      ],
      rows: data.rows.map((r) => [
        r.employee_name,
        { value: (r.commission_amount || 0).toFixed(2), className: "num" },
        { value: (r.tips_amount || 0).toFixed(2), className: "num" },
        { value: (r.net || 0).toFixed(2), className: "num pos" },
        r.status,
      ]),
      totals: [
        { label: lang === "ar" ? "إجمالي العمولات" : "Total commission", value: (data.totals.commission || 0).toFixed(2) },
        { label: lang === "ar" ? "إجمالي الإكراميات" : "Total tips", value: (data.totals.tips || 0).toFixed(2) },
        { label: lang === "ar" ? "الصافي" : "Net", value: (data.totals.net || 0).toFixed(2), className: "gold" },
      ],
    });
  };

  return (
    <div className="space-y-6" data-testid="staff-payouts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-4xl gold-text">{t("nav_staff_payouts")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
            <input
              type="checkbox"
              checked={customRange}
              onChange={(e) => {
                const on = e.target.checked;
                setCustomRange(on);
                if (on) {
                  setFromDate(monthStart(month));
                  setToDate(monthEnd(month));
                }
              }}
            />
            {lang === "ar" ? "فترة مخصصة" : "Custom range"}
          </label>
          {customRange ? (
            <>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="input-dark num-display" data-testid="payout-from" />
              <span className="text-[var(--text-muted)]">→</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="input-dark num-display" data-testid="payout-to" />
            </>
          ) : (
            <select value={month} onChange={(e) => setMonth(e.target.value)} className="input-dark num-display" data-testid="payout-month">
              {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className="input-dark" data-testid="payout-account">
            <option value="">{lang === "ar" ? "صندوق الدفع" : "Payout account"}</option>
            {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
          </select>
          <button onClick={exportCsv} className="btn-soft px-3 py-1.5 rounded text-sm flex items-center gap-1"><Download size={13}/>CSV</button>
          <button onClick={printReportPro} className="btn-soft px-3 py-1.5 rounded text-sm flex items-center gap-1"><Printer size={13}/>{lang === "ar" ? "طباعة" : "Print"}</button>
          <button onClick={confirmAll} className="btn-gold px-3 py-1.5 rounded text-sm flex items-center gap-1" data-testid="confirm-all-payouts">
            <CheckCircle size={13}/>{lang === "ar" ? "تأكيد الكل" : "Confirm all"}
          </button>
        </div>
      </div>

      <div className="card-surface p-4 text-sm border border-[var(--gold)]/20 bg-[var(--gold-tint)]/30">
        <div className="font-medium text-[var(--text)] mb-1">{t("payouts_page_title")}</div>
        <p className="text-[var(--text-muted)]">{t("payouts_page_help")}</p>
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          <Link to="/commissions" className="text-[var(--gold)] hover:underline">{t("payouts_view_reports")} →</Link>
          <Link to="/payroll" className="text-[var(--gold)] hover:underline">{lang === "ar" ? "الرواتب ←" : "Payroll →"}</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><Users size={11}/>{lang === "ar" ? "الموظفون" : "Staff"}</div>
          <div className="text-2xl font-bold mt-1">{data.rows.length}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase text-[var(--text-muted)]">{t("commission_owed")}</div>
          <div className="text-2xl font-bold mt-1 num-display">{fmt(data.totals.commission)}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase text-[var(--text-muted)]">{t("tips_owed")}</div>
          <div className="text-2xl font-bold mt-1 num-display">{fmt(data.totals.tips)}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><Wallet size={11}/>{lang === "ar" ? "الصافي" : "Net"}</div>
          <div className="text-2xl font-bold mt-1 gold-text num-display">{fmt(data.totals.net)}</div>
        </div>
      </div>

      <div className="card-surface p-4 overflow-x-auto">
        <table className="table-dark min-w-full">
          <thead>
            <tr>
              <th className="w-8" />
              <th>{lang === "ar" ? "الموظف" : "Employee"}</th>
              <th>{t("commission_owed")}</th>
              <th>{t("tips_owed")}</th>
              <th>{lang === "ar" ? "تعديلات" : "Adj."}</th>
              <th>{lang === "ar" ? "الصافي" : "Net"}</th>
              <th>{lang === "ar" ? "الحالة" : "Status"}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const ex = expanded[r.employee_user_id];
              const payable = r.net > 0 && r.status !== "confirmed";
              const statusLabel = r.status === "draft"
                ? (lang === "ar" ? "مسودة" : "Draft")
                : r.status === "supplemental"
                  ? (lang === "ar" ? "تسوية إضافية" : "Supplemental")
                  : (lang === "ar" ? "مفتوح" : "Open");
              return (
                <React.Fragment key={r.employee_user_id}>
                  <tr data-testid={`payout-row-${r.employee_user_id}`}>
                    <td>
                      <button type="button" onClick={() => setExpanded((p) => ({ ...p, [r.employee_user_id]: !p[r.employee_user_id] }))} className="p-1">
                        {ex ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                      </button>
                    </td>
                    <td>
                      <div className="font-medium">{r.employee_name}</div>
                      {!r.hr_linked && (
                        <div className="text-[10px] text-amber-600">{lang === "ar" ? "غير مرتبط بـ HR" : "Not linked in HR"}</div>
                      )}
                      {(r.prior_payout_count || 0) > 0 && (
                        <div className="text-[10px] text-[var(--text-muted)]">
                          {lang === "ar"
                            ? `مدفوع سابقاً: ${fmt(r.prior_paid_net)} (${r.prior_payout_count})`
                            : `Paid earlier: ${fmt(r.prior_paid_net)} (${r.prior_payout_count})`}
                        </div>
                      )}
                    </td>
                    <td className="num-display">{fmt(r.commission_amount)}</td>
                    <td className="num-display">{fmt(r.tips_amount)}</td>
                    <td className="num-display text-xs">
                      {(r.total_bonuses || 0) > 0 && <span className="gold-text">+{fmt(r.total_bonuses)} </span>}
                      {(r.total_deductions || 0) > 0 && <span className="rose-text">−{fmt(r.total_deductions)}</span>}
                    </td>
                    <td className="num-display font-bold gold-text">{fmt(r.net)}</td>
                    <td>
                      {r.status === "draft"
                        ? <span className="badge badge-warning">{statusLabel}</span>
                        : r.status === "supplemental"
                          ? <span className="badge badge-info">{statusLabel}</span>
                          : <span className="badge">{statusLabel}</span>}
                    </td>
                    <td className="text-end whitespace-nowrap">
                      {payable && (
                        <>
                          <button type="button" onClick={() => { setAdjModal(r.employee_user_id); setAdjForm({ type: "bonus", amount: 0, reason: "" }); }}
                            className="btn-soft px-2 py-1 rounded text-xs me-1 inline-flex items-center gap-1">
                            <Plus size={12}/>{lang === "ar" ? "قيد" : "Adjust"}
                          </button>
                          <button type="button" onClick={() => confirmPayout(r.employee_user_id)} className="btn-gold px-2 py-1 rounded text-xs inline-flex items-center gap-1" data-testid={`confirm-payout-${r.employee_user_id}`}>
                            <CheckCircle size={12}/>{t("confirm_payout")}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {ex && (
                    <tr className="bg-[var(--bg)]/50">
                      <td colSpan={8} className="p-3 text-xs space-y-2">
                        <div className="font-medium">{lang === "ar" ? "عمولات غير مدفوعة" : "Unpaid commissions"}: {(r.commission_accruals || []).length}</div>
                        <div className="font-medium">{lang === "ar" ? "إكراميات غير مدفوعة" : "Unpaid tips"}: {(r.tip_accruals || []).length}</div>
                        {(r.prior_payout_count || 0) > 0 && (
                          <div className="text-[var(--text-muted)]">
                            {lang === "ar" ? "مدفوع سابقاً في هذه الفترة" : "Previously paid in this period"}:{" "}
                            {fmt(r.prior_paid_commission)} {lang === "ar" ? "عمولة" : "comm."} + {fmt(r.prior_paid_tips)} {t("tip")}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {data.rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-[var(--text-muted)]">{lang === "ar" ? "لا توجد مستحقات في هذه الفترة" : "No accruals in this period"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {history.length > 0 && (
        <div className="card-surface p-4 overflow-x-auto">
          <h2 className="font-heading text-lg gold-text mb-3">{lang === "ar" ? "سجل الصرف" : "Payout history"}</h2>
          <table className="table-dark min-w-full text-sm">
            <thead>
              <tr>
                <th>{lang === "ar" ? "الموظف" : "Employee"}</th>
                <th>{t("commission_owed")}</th>
                <th>{t("tips_owed")}</th>
                <th>{lang === "ar" ? "الصافي" : "Net"}</th>
                <th>{lang === "ar" ? "تاريخ التأكيد" : "Confirmed"}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.map((p) => (
                <tr key={p.payout_id}>
                  <td>{p.employee_name}</td>
                  <td className="num-display">{fmt(p.commission_amount)}</td>
                  <td className="num-display">{fmt(p.tips_amount)}</td>
                  <td className="num-display font-bold">{fmt(p.net)}</td>
                  <td className="text-xs text-[var(--text-muted)]">{(p.confirmed_at || "").slice(0, 10)}</td>
                  <td className="text-end">
                    <button type="button" onClick={() => unconfirm(p.payout_id)} className="btn-ghost px-2 py-1 rounded text-xs inline-flex items-center gap-1">
                      <RotateCcw size={12}/>{lang === "ar" ? "إلغاء" : "Unconfirm"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adjModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setAdjModal(null))}>
          <div className="card-surface p-6 w-full max-w-md space-y-4" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-xl gold-text">{lang === "ar" ? "تعديل الصرف" : "Payout adjustment"}</h2>
            <select className="input-dark" value={adjForm.type} onChange={(e) => setAdjForm({ ...adjForm, type: e.target.value })}>
              <option value="bonus">{lang === "ar" ? "مكافأة" : "Bonus"}</option>
              <option value="deduction">{lang === "ar" ? "خصم" : "Deduction"}</option>
            </select>
            <input type="number" step="0.01" className="input-dark num-display" value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} />
            <input className="input-dark" placeholder={lang === "ar" ? "السبب" : "Reason"} value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAdjModal(null)} className="btn-ghost px-4 py-2 rounded">{t("cancel")}</button>
              <button type="button" onClick={addAdjustment} className="btn-gold px-4 py-2 rounded">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
